import {
  ClipboardList,
  Calendar,
  ExternalLink,
  FileText,
  Tag,
  Plus,
} from "lucide-react";
import { Badge } from "@/components/ui";
import { useChatContext } from "@/stores/chat-context-store";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  AUDIT_STATUS_CONFIG,
  formatDeadline,
  completionColor,
  completionBarColor,
} from "./chatCardUtils";
import type { AuditDetailResult } from "./chatCardUtils";
import type { AuditStatus } from "@/packages/types";

function isAuditStatus(s: string): s is AuditStatus {
  return [
    "pending",
    "in_progress",
    "submitted",
    "approved",
    "rejected",
    "archived",
  ].includes(s);
}

/** Shared fields used for rendering (both list item and detail result have these) */
interface AuditCardData {
  id: string;
  displayName: string;
  code: string;
  status: string;
  deadline: number;
  templateName: string;
  completionPercent: number;
}

interface AuditCardProps {
  audit: AuditCardData;
  variant?: "compact" | "detail";
  detail?: AuditDetailResult;
}

export function AuditCard({
  audit,
  variant = "compact",
  detail,
}: AuditCardProps) {
  const { addEntity } = useChatContext();
  const navigate = useNavigate();
  const { workspaceSlug } = useParams({ strict: false });
  const statusConfig = isAuditStatus(audit.status)
    ? AUDIT_STATUS_CONFIG[audit.status]
    : undefined;

  const handleClick = () => {
    void navigate({
      to: "/$workspaceSlug/audits/$auditId",
      params: { workspaceSlug: workspaceSlug ?? "", auditId: audit.id },
    });
  };

  const handleAddToContext = (e: React.MouseEvent) => {
    e.stopPropagation();
    addEntity({
      type: "audit",
      id: audit.id,
      label: audit.displayName,
    });
  };

  return (
    <div className="relative flex items-start gap-3 w-full p-3 rounded-lg border border-border/60 bg-surface hover:bg-accent/10 hover:border-accent/30 transition-all text-left group cursor-pointer">
      <button
        type="button"
        onClick={handleClick}
        className="absolute inset-0 rounded-lg"
        aria-label={`Open ${audit.displayName}`}
      />
      <div className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent/10">
        <ClipboardList size={16} className="text-accent" />
      </div>
      <div className="relative flex-1 min-w-0">
        {/* Title row */}
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-medium text-primary truncate">
            {audit.displayName}
          </span>
        </div>

        {/* Code + status */}
        <div className="flex items-center gap-2 mt-1">
          <span className="text-[10px] font-medium text-muted uppercase tracking-wider bg-card rounded px-1.5 py-0.5">
            {audit.code}
          </span>
          {statusConfig && (
            <Badge
              color={{ bg: statusConfig.badgeBg, text: statusConfig.badgeText }}
              className="px-2 py-0.5 text-[10px]"
            >
              {statusConfig.label}
            </Badge>
          )}
        </div>

        {/* Template + deadline + completion */}
        <div className="flex items-center gap-2.5 mt-1.5 text-[11px] text-muted">
          <span className="truncate">{audit.templateName}</span>
          <span className="shrink-0 inline-flex items-center gap-0.5">
            <Calendar size={10} />
            {formatDeadline(audit.deadline)}
          </span>
          <span
            className={`shrink-0 font-semibold ${completionColor(audit.completionPercent)}`}
          >
            {audit.completionPercent}%
          </span>
        </div>

        {/* Detail variant extras */}
        {variant === "detail" && detail && (
          <div className="mt-2 space-y-1.5">
            {detail.description && (
              <p className="text-xs text-muted line-clamp-2">
                {detail.description}
              </p>
            )}

            {/* Progress bar */}
            <div className="w-full h-1.5 rounded-full bg-border/30 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${completionBarColor(detail.completionPercent)}`}
                style={{ width: `${detail.completionPercent}%` }}
              />
            </div>

            {/* Item counts */}
            <div className="flex items-center gap-3 text-[10px] text-muted">
              <span className="text-emerald-500">
                {detail.completedItems} approved
              </span>
              <span className="text-amber-500">
                {detail.uploadedItems} uploaded
              </span>
              <span>{detail.missingItems} missing</span>
              <span className="inline-flex items-center gap-0.5">
                <FileText size={10} />
                {detail.totalDocuments} docs
              </span>
            </div>

            {/* Tags */}
            {detail.tags.length > 0 && (
              <div className="flex items-center gap-1 flex-wrap">
                <Tag size={10} className="text-muted shrink-0" />
                {detail.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-[10px] text-muted bg-card rounded px-1.5 py-0.5"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {/* AI summary excerpt */}
            {detail.aiSummaryExcerpt && (
              <p className="text-[11px] text-muted italic line-clamp-2 border-l-2 border-accent/30 pl-2">
                {detail.aiSummaryExcerpt}
              </p>
            )}
          </div>
        )}
      </div>
      <div className="relative flex flex-col items-center gap-1 shrink-0 self-start">
        <span className="flex items-center gap-0.5 text-[10px] text-muted opacity-0 group-hover:opacity-100 transition-opacity">
          <ExternalLink size={10} />
          Open
        </span>
        <button
          type="button"
          onClick={handleAddToContext}
          title="Add to chat context"
          className="z-10 flex items-center justify-center h-6 w-6 rounded opacity-0 group-hover:opacity-100 transition-opacity hover:bg-accent/10 text-muted hover:text-accent"
        >
          <Plus size={13} />
        </button>
      </div>
    </div>
  );
}
